import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Phone,
  Mail,
  Instagram,
  Youtube,
  Facebook,
  MapPin,
  Clock,
  Star,
  Shield,
  Award,
  CheckCircle,
  Menu,
  X,
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import ServiceCard from "../components/ServiceCard";
import ProcessStep from "../components/ProcessStep";
import { MOCK_DATA } from "../utils/mock";
import { WhatsAppIcon } from "../components/WhatsAppIcon";

const NAV_ITEMS = [
  { id: "services", label: "Services" },
  { id: "add-ons", label: "Add-Ons" },
  { id: "process", label: "Process" },
  { id: "why-us", label: "Why Us" },
  { id: "testimonials", label: "Reviews" },
  { id: "contact", label: "Contact" },
];

const HERO_IMAGES = [
  // Your local optimized hero images (public/img/hero-slides).
  "/img/hero-slides/hero-slide-02.webp",
  "/img/hero-slides/hero-slide-03.webp",
  "/img/hero-slides/hero-slide-04.webp",
  "/img/hero-slides/hero-slide-05.webp",
  "/img/hero-slides/hero-slide-06.webp",
  "/img/hero-slides/hero-slide-07.webp",
  "/img/hero-slides/hero-slide-08.webp",
  "/img/hero-slides/hero-slide-09.webp",
  "/img/hero-slides/hero-slide-10.webp",
  "/img/hero-slides/hero-slide-11.webp",
  "/img/hero-slides/hero-slide-12.webp",
  "/img/hero-slides/hero-slide-13.webp",

  "/img/hero-slides/hero-slide-15.webp",

  "/img/hero-slides/hero-slide-17.webp",
  "/img/hero-slides/hero-slide-18.webp",
  "/img/hero-slides/hero-slide-19.webp",
  "/img/hero-slides/hero-slide-20.webp",
  "/img/hero-slides/hero-slide-21.webp",
  "/img/hero-slides/hero-slide-22.webp",
];

const Home = () => {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentHeroImage, setCurrentHeroImage] = useState(0);
  const [previousHeroImage, setPreviousHeroImage] = useState(null);
  const [isMobileViewport, setIsMobileViewport] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [shouldLoadInstagram, setShouldLoadInstagram] = useState(false);
  const headerOffsetRef = useRef(96);
  const gallerySectionRef = useRef(null);

  useEffect(() => {
    if (window.innerWidth < 1280) {
      setScrolled(true);
      return undefined;
    }
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleViewportResize = () => {
      setIsMobileViewport(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleViewportResize);
    return () => window.removeEventListener("resize", handleViewportResize);
  }, []);

  useEffect(() => {
    if (window.innerWidth < 1280) {
      setActiveSection("");
      return undefined;
    }

    const sectionIds = NAV_ITEMS.map((item) => item.id);
    let sectionPositions = [];
    let ticking = false;

    const calculateSectionPositions = () => {
      sectionPositions = sectionIds
        .map((id) => {
          const section = document.getElementById(id);
          return section ? { id, top: section.offsetTop } : null;
        })
        .filter(Boolean);
    };

    const updateActiveSection = () => {
      const scrollPosition = window.scrollY + headerOffsetRef.current + 24;
      let current = sectionPositions[0]?.id || "";
      for (let i = 0; i < sectionPositions.length; i += 1) {
        if (sectionPositions[i].top <= scrollPosition) {
          current = sectionPositions[i].id;
        } else {
          break;
        }
      }
      setActiveSection((prev) => (prev === current ? prev : current));
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        updateActiveSection();
        ticking = false;
      });
    };

    const onResize = () => {
      calculateSectionPositions();
      onScroll();
    };

    calculateSectionPositions();
    updateActiveSection();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  useEffect(() => {
    const header = document.querySelector('header[role="banner"]');
    if (!header) return undefined;

    const updateHeaderOffset = () => {
      headerOffsetRef.current = Math.ceil(header.getBoundingClientRect().height) + 12;
    };

    updateHeaderOffset();
    window.addEventListener("resize", updateHeaderOffset);
    let resizeObserver;
    if ("ResizeObserver" in window) {
      resizeObserver = new ResizeObserver(updateHeaderOffset);
      resizeObserver.observe(header);
    }

    return () => {
      window.removeEventListener("resize", updateHeaderOffset);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1280) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setPrefersReducedMotion(mediaQuery.matches);
    onChange();
    mediaQuery.addEventListener("change", onChange);
    return () => mediaQuery.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (HERO_IMAGES.length <= 1 || prefersReducedMotion) {
      return undefined;
    }
    const interval = window.setInterval(() => {
      setCurrentHeroImage((prev) => {
        setPreviousHeroImage(prev);
        return (prev + 1) % HERO_IMAGES.length;
      });
    }, 5000);
    return () => window.clearInterval(interval);
  }, [prefersReducedMotion]);

  useEffect(() => {
    if (previousHeroImage === null || prefersReducedMotion) {
      return undefined;
    }
    const timeout = window.setTimeout(() => {
      setPreviousHeroImage(null);
    }, 1800);
    return () => window.clearTimeout(timeout);
  }, [previousHeroImage, prefersReducedMotion]);

  const getHeroImageSource = useCallback(
    (index) => {
      const source = HERO_IMAGES[index];
      if (!source) return source;
      if (!isMobileViewport) return source;
      return source.endsWith(".webp")
        ? source.replace(".webp", "-m.webp")
        : source;
    },
    [isMobileViewport]
  );

  useEffect(() => {
    if (prefersReducedMotion || HERO_IMAGES.length <= 1) {
      return;
    }
    const nextImage = getHeroImageSource(
      (currentHeroImage + 1) % HERO_IMAGES.length
    );
    const img = new Image();
    img.src = nextImage;
  }, [currentHeroImage, getHeroImageSource, prefersReducedMotion]);

  useEffect(() => {
    if (!gallerySectionRef.current || shouldLoadInstagram) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShouldLoadInstagram(true);
          }
        });
      },
      { rootMargin: "300px 0px" }
    );

    observer.observe(gallerySectionRef.current);
    return () => observer.disconnect();
  }, [shouldLoadInstagram]);

  useEffect(() => {
    if (!shouldLoadInstagram) {
      return;
    }
    const scriptId = "EmbedSocialHashtagScript";
    if (document.getElementById(scriptId)) {
      return;
    }
    const script = document.createElement("script");
    script.id = scriptId;
    script.src = "https://embedsocial.com/cdn/ht.js";
    script.async = true;
    document.head.appendChild(script);
  }, [shouldLoadInstagram]);

  const getHeaderOffset = () => {
    return headerOffsetRef.current || 96;
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const yPosition =
        element.getBoundingClientRect().top + window.scrollY - getHeaderOffset();
      window.scrollTo({ top: Math.max(yPosition, 0), behavior: "smooth" });
      setActiveSection(id);
      setMobileMenuOpen(false);
    }
  };

  const faqSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: MOCK_DATA.faqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    }),
    []
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-neutral-900 to-black text-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      {/* Header */}
      <header className="fixed top-0 w-full z-50" role="banner">
        <div className="container mx-auto px-3 sm:px-2 pt-3 sm:pt-4">
          <div
            className={`rounded-full border border-white/10 transition-all duration-300 ${
              scrolled
                ? "bg-black/70 backdrop-blur-2xl shadow-[0_20px_55px_rgba(0,0,0,0.45)]"
                : "bg-white/5 backdrop-blur-md shadow-[0_12px_36px_rgba(0,0,0,0.3)]"
            }`}
          >
            <div className="px-2 sm:ps-2 xl:ps-2 sm:pe-3 xl:pe-4 py-1.5 sm:py-2">
              <div className="flex items-center justify-between gap-2 xl:gap-4">
                <button
                  type="button"
                  onClick={() => scrollToSection("home")}
                  className="flex items-center space-x-2.5 min-w-0 text-left"
                  aria-label="Go to top section"
                >
                  <img
                    src="/img/logo-site.webp"
                    alt="Detailing Bhai Logo - Best Car Detailing in Jaipur"
                    className="h-12 w-12 sm:h-14 sm:w-14 xl:h-[72px] xl:w-[72px] object-contain flex-shrink-0"
                    width="64"
                    height="64"
                  />
                  <div className="min-w-0">
                    <p className="db-brand-gradient text-[1.35rem] sm:text-[1.55rem] font-bold mb-0 leading-none">
                      DETAILING BHAI
                    </p>
                    <p className="hidden 2xl:block text-sm text-neutral-300">
                      Jaipur's Trusted Car Detailing Studio
                    </p>
                  </div>
                </button>

                <nav
                  className="hidden xl:flex items-center gap-0.5"
                  role="navigation"
                  aria-label="Main navigation"
                >
                  {NAV_ITEMS.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      className={`px-3 2xl:px-4 py-1.5 rounded-full text-sm font-semibold transition-all duration-300 ${
                        activeSection === item.id
                          ? "bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-[0_10px_30px_rgba(249,115,22,0.35)]"
                          : "text-neutral-200 hover:text-white hover:bg-white/10"
                      }`}
                      aria-current={
                        activeSection === item.id ? "page" : undefined
                      }
                    >
                      {item.label}
                    </button>
                  ))}
                </nav>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <a
                    href="tel:9773301727"
                    className="hidden 2xl:flex items-center space-x-2 bg-gradient-to-r from-orange-500 to-amber-500 px-3 py-1.5 rounded-full hover:from-orange-600 hover:to-amber-600 transition-all hover:scale-105"
                  >
                    <Phone className="h-4 w-4" />
                    <span className="font-semibold">Call Now</span>
                  </a>
                  <a
                    href="https://wa.me/919773301727"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hidden 2xl:flex items-center space-x-2 bg-gradient-to-r from-green-500 to-emerald-500 px-3 py-1.5 rounded-full hover:from-green-600 hover:to-emerald-600 transition-all hover:scale-105"
                  >
                    <WhatsAppIcon className="h-4 w-4" />
                    <span className="font-semibold">WhatsApp</span>
                  </a>

                  <a
                    href="tel:9773301727"
                    className="hidden sm:flex 2xl:hidden items-center justify-center h-9 w-9 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 transition-all"
                    aria-label="Call Now"
                  >
                    <Phone className="h-4 w-4 text-white" />
                  </a>
                  <a
                    href="https://wa.me/919773301727"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hidden sm:flex 2xl:hidden items-center justify-center h-9 w-9 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 transition-all"
                    aria-label="WhatsApp"
                  >
                    <WhatsAppIcon className="h-4 w-4 text-white" />
                  </a>

                  <button
                    type="button"
                    className="xl:hidden inline-flex items-center justify-center h-9 w-9 rounded-full border border-white/20 bg-white/5 text-white hover:bg-white/10 transition-all"
                    onClick={() => setMobileMenuOpen((prev) => !prev)}
                    aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
                  >
                    {mobileMenuOpen ? (
                      <X className="h-5 w-5" />
                    ) : (
                      <Menu className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {mobileMenuOpen && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 bg-black/70 xl:hidden"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close menu overlay"
          />
          <div className="fixed z-50 top-[76px] sm:top-[88px] left-3 right-3 rounded-2xl border border-white/15 bg-black/95 backdrop-blur-xl p-3 xl:hidden shadow-2xl">
            <nav className="grid grid-cols-2 gap-2" aria-label="Mobile navigation">
              {NAV_ITEMS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => scrollToSection(item.id)}
                  className={`rounded-xl px-3 py-2.5 text-sm font-semibold text-left transition-all ${
                    activeSection === item.id
                      ? "bg-gradient-to-r from-orange-500 to-amber-500 text-white"
                      : "bg-white/5 text-neutral-200 hover:bg-white/10"
                  }`}
                  aria-current={activeSection === item.id ? "page" : undefined}
                >
                  {item.label}
                </button>
              ))}
            </nav>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <a
                href="tel:9773301727"
                onClick={() => setMobileMenuOpen(false)}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 px-4 py-3 font-semibold text-white"
              >
                <Phone className="h-4 w-4" />
                Call Now
              </a>
              <a
                href="https://wa.me/919773301727"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileMenuOpen(false)}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 px-4 py-3 font-semibold text-white"
              >
                <WhatsAppIcon className="h-4 w-4" />
                WhatsApp
              </a>
            </div>
          </div>
        </>
      )}

      {/* Hero Section */}
      <section
        id="home"
        className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20"
        role="main"
        aria-label="Hero section"
      >
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/10 to-black z-10"></div>
          <img
            key={`current-${prefersReducedMotion ? 0 : currentHeroImage}`}
            src={getHeroImageSource(prefersReducedMotion ? 0 : currentHeroImage)}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 z-0 w-full h-full object-cover object-center opacity-100"
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
          {previousHeroImage !== null && !prefersReducedMotion ? (
            <img
              key={`prev-${previousHeroImage}`}
              src={getHeroImageSource(previousHeroImage)}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 z-[1] w-full h-full object-cover object-center will-change-[opacity]"
              style={{ animation: "heroFadeOut 1800ms ease-in-out forwards" }}
              loading="lazy"
              fetchPriority="low"
              decoding="async"
            />
          ) : null}
        </div>

        <div className="relative z-20 container mx-auto px-4 text-center">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-3xl md:text-7xl font-bold leading-[1.18] md:leading-[1.2]">
              <span className="hero-title-shadow">The </span>
              <span className="bg-gradient-to-r from-amber-400 via-orange-500 to-yellow-500 bg-clip-text text-transparent">
                #1 Detailing Studio
              </span>
              <span className="hero-title-shadow">
                {" "}
                for Ceramic Coating &amp; PPF in Jaipur
              </span>
            </h1>
            <div className="mt-6 mx-auto w-full max-w-[22rem] sm:max-w-none">
              <div className="grid grid-cols-3 gap-2 sm:flex sm:justify-center sm:gap-4">
                <div className="flex items-center justify-center gap-1.5 sm:gap-2 bg-white/10 backdrop-blur-sm px-2 sm:px-4 py-2 rounded-full text-[0.78rem] sm:text-base whitespace-nowrap">
                  <Award className="h-4 w-4 sm:h-5 sm:w-5 text-orange-500" />
                  <span className="sm:hidden">15+ Years</span>
                  <span className="hidden sm:inline">15+ Years Expertise</span>
                </div>
                <div className="flex items-center justify-center gap-1.5 sm:gap-2 bg-white/10 backdrop-blur-sm px-2 sm:px-4 py-2 rounded-full text-[0.78rem] sm:text-base whitespace-nowrap">
                  <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-orange-500" />
                  <span className="sm:hidden">100% Safe</span>
                  <span className="hidden sm:inline">100% Guaranteed</span>
                </div>
                <div className="flex items-center justify-center gap-1.5 sm:gap-2 bg-white/10 backdrop-blur-sm px-2 sm:px-4 py-2 rounded-full text-[0.78rem] sm:text-base whitespace-nowrap">
                  <Star className="h-4 w-4 sm:h-5 sm:w-5 text-orange-500" />
                  <span className="sm:hidden">7-Stage</span>
                  <span className="hidden sm:inline">7-Stage Process</span>
                </div>
              </div>
            </div>

            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:9773301727"
                className="bg-gradient-to-r from-orange-500 to-amber-500 text-white px-8 py-4 rounded-full text-lg font-semibold hover:from-orange-600 hover:to-amber-600 transition-all hover:scale-105 inline-flex items-center justify-center space-x-2"
              >
                <Phone className="h-5 w-5" />
                <span>Call Now: 9773301727</span>
              </a>
              <button
                onClick={() => scrollToSection("contact")}
                className="bg-white/10 backdrop-blur-sm border border-white/20 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white/20 transition-all inline-flex items-center justify-center"
              >
                Get Free Consultation
              </button>
            </div>
          </div>
        </div>

        <div
          className={`absolute bottom-10 left-1/2 transform -translate-x-1/2 z-20 ${
            prefersReducedMotion ? "" : "animate-bounce"
          }`}
        >
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-white/70 rounded-full"></div>
          </div>
        </div>
      </section>

      <div className="db-main-surface">
      {/* Services Section */}
      <section
        id="services"
        className="py-20 db-section-divider"
        aria-label="Our premium car detailing services"
      >
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Our{" "}
              <span className="db-heading-accent bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">
                Premium Services
              </span>
            </h2>
            <p className="text-neutral-400 text-lg max-w-3xl mx-auto">
              Market-first technology and expert craftsmanship for complete
              vehicle protection in Jaipur
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {MOCK_DATA.services.map((service, index) => (
              <ServiceCard key={index} service={service} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Complete Car Care Solutions Section */}
      <section
        id="care-solutions"
        className="py-20 db-section-divider"
        aria-label="Complete car care solutions"
      >
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Complete{" "}
              <span className="db-heading-accent bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">
                Car Care Solutions
              </span>
            </h2>
            <p className="text-neutral-300 text-lg max-w-3xl mx-auto">
              Beyond detailing packages, we offer practical upgrades and
              maintenance essentials that keep your vehicle protected,
              comfortable, and road-ready every day.
            </p>
          </div>

          <div className="max-w-6xl mx-auto grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {MOCK_DATA.additionalServices.map((service, index) => (
              <Card
                key={index}
                className="db-orb-card bg-white/5 border-white/10 hover:bg-white/10 transition-all duration-300 hover:scale-[1.02] hover:-translate-y-1 hover:shadow-[0_16px_45px_rgba(249,115,22,0.12)]"
              >
                <CardContent className="p-4 sm:p-5 flex items-center gap-3">
                  <CheckCircle className="h-6 w-6 sm:h-7 sm:w-7 text-orange-500 flex-shrink-0" />
                  <p className="text-white font-bold text-base leading-snug">
                    {service}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Complimentary Addon Services Section */}
      <section
        id="add-ons"
        className="py-20 db-section-divider"
      >
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Premium{" "}
              <span className="db-heading-accent bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">
                Add-On Services
              </span>
            </h2>
            <p className="text-neutral-300 text-lg max-w-4xl mx-auto">
              Practical premium services, included{" "}
              <span className="font-bold text-white">FREE</span> with every
              Ceramic Coating and PPF package, worth approximately{" "}
              <span className="font-bold text-amber-300">₹15,000 - ₹25,000</span>{" "}
              at no extra cost.
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Deep Cleaning */}
              <Card className="db-orb-card relative bg-gradient-to-r from-white/5 to-white/10 border-white/10 hover:border-orange-500/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_50px_rgba(249,115,22,0.12)]">
                <CardContent className="p-6 db-feature-row">
                  <div className="bg-gradient-to-br from-orange-500 to-amber-500 p-2.5 sm:p-3 rounded-lg flex-shrink-0">
                    <CheckCircle className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
                  </div>
                  <div className="db-feature-copy">
                    <h3 className="db-feature-title text-xl font-bold text-white mb-2">
                      Complete Deep Cleaning
                    </h3>
                    <p className="text-neutral-300">
                      Comprehensive interior and exterior deep cleaning
                      including all surfaces, crevices, and hard-to-reach areas.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Leather Seats Coating */}
              <Card className="db-orb-card relative bg-gradient-to-r from-white/5 to-white/10 border-white/10 hover:border-orange-500/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_50px_rgba(249,115,22,0.12)]">
                <CardContent className="p-6 db-feature-row">
                  <div className="bg-gradient-to-br from-orange-500 to-amber-500 p-2.5 sm:p-3 rounded-lg flex-shrink-0">
                    <CheckCircle className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
                  </div>
                  <div className="db-feature-copy">
                    <h3 className="db-feature-title text-xl font-bold text-white mb-2">
                      Leather Seats Ceramic Coating
                    </h3>
                    <p className="text-neutral-300">
                      Professional ceramic coating applied on all leather seats
                      providing advanced protection against stains, spills, UV
                      damage, and everyday wear and tear.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Dashboard & Interior Coating */}
              <Card className="db-orb-card relative bg-gradient-to-r from-white/5 to-white/10 border-white/10 hover:border-orange-500/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_50px_rgba(249,115,22,0.12)]">
                <CardContent className="p-6 db-feature-row">
                  <div className="bg-gradient-to-br from-orange-500 to-amber-500 p-2.5 sm:p-3 rounded-lg flex-shrink-0">
                    <CheckCircle className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
                  </div>
                  <div className="db-feature-copy">
                    <h3 className="db-feature-title text-xl font-bold text-white mb-2">
                      Dashboard & Gloss Parts Coating
                    </h3>
                    <p className="text-neutral-300">
                      Ceramic coating application on dashboard, center console,
                      and all interior glossy surfaces for lasting protection.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Alloy Wheels Coating */}
              <Card className="db-orb-card relative bg-gradient-to-r from-white/5 to-white/10 border-white/10 hover:border-orange-500/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_50px_rgba(249,115,22,0.12)]">
                <CardContent className="p-6 db-feature-row">
                  <div className="bg-gradient-to-br from-orange-500 to-amber-500 p-2.5 sm:p-3 rounded-lg flex-shrink-0">
                    <CheckCircle className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
                  </div>
                  <div className="db-feature-copy">
                    <h3 className="db-feature-title text-xl font-bold text-white mb-2">
                      Alloy Wheels Ceramic Coating
                    </h3>
                    <p className="text-neutral-300">
                      High-heat resistant ceramic coating on all alloy wheels
                      for protection against brake dust and environmental
                      contaminants.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Plastic Claddings Coating */}
              <Card className="db-orb-card relative bg-gradient-to-r from-white/5 to-white/10 border-white/10 hover:border-orange-500/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_50px_rgba(249,115,22,0.12)]">
                <CardContent className="p-6 db-feature-row">
                  <div className="bg-gradient-to-br from-orange-500 to-amber-500 p-2.5 sm:p-3 rounded-lg flex-shrink-0">
                    <CheckCircle className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
                  </div>
                  <div className="db-feature-copy">
                    <h3 className="db-feature-title text-xl font-bold text-white mb-2">
                      Plastic Claddings Black Coating
                    </h3>
                    <p className="text-neutral-300">
                      Specialized black coating on all exterior plastic
                      claddings, bumper trim pieces, and unpainted surfaces to
                      restore deep black color and provide long-lasting
                      protection.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Glass Ceramic Coating */}
              <Card className="db-orb-card relative bg-gradient-to-r from-white/5 to-white/10 border-white/10 hover:border-orange-500/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_50px_rgba(249,115,22,0.12)]">
                <CardContent className="p-6 db-feature-row">
                  <div className="bg-gradient-to-br from-orange-500 to-amber-500 p-2.5 sm:p-3 rounded-lg flex-shrink-0">
                    <CheckCircle className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
                  </div>
                  <div className="db-feature-copy">
                    <h3 className="db-feature-title text-xl font-bold text-white mb-2">
                      Complete Glass Ceramic Coating
                    </h3>
                    <p className="text-neutral-300">
                      Hydrophobic ceramic coating on windshield, side windows,
                      and rear glass for superior water repellency and clarity.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Paint Correction & Polishing */}
              <Card className="db-orb-card relative bg-gradient-to-r from-white/5 to-white/10 border-white/10 hover:border-orange-500/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_50px_rgba(249,115,22,0.12)]">
                <CardContent className="p-6 db-feature-row">
                  <div className="bg-gradient-to-br from-orange-500 to-amber-500 p-2.5 sm:p-3 rounded-lg flex-shrink-0">
                    <CheckCircle className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
                  </div>
                  <div className="db-feature-copy">
                    <h3 className="db-feature-title text-xl font-bold text-white mb-2">
                      Paint Correction & Full Car Polishing
                    </h3>
                    <p className="text-neutral-300">
                      Professional removal of minor scratches and swirl marks
                      using premium mild compound, followed by complete machine
                      polishing for a flawless mirror finish across the entire
                      vehicle.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Engine Bay Detailing & Dressing */}
              <Card className="db-orb-card relative bg-gradient-to-r from-white/5 to-white/10 border-white/10 hover:border-orange-500/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_50px_rgba(249,115,22,0.12)]">
                <CardContent className="p-6 db-feature-row">
                  <div className="bg-gradient-to-br from-orange-500 to-amber-500 p-2.5 sm:p-3 rounded-lg flex-shrink-0">
                    <CheckCircle className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
                  </div>
                  <div className="db-feature-copy">
                    <h3 className="db-feature-title text-xl font-bold text-white mb-2">
                      Engine Bay Detailing & Dressing
                    </h3>
                    <p className="text-neutral-300">
                      Complete engine bay cleaning with professional degreasing,
                      followed by application of premium rubber and plastic
                      dressing to protect hoses, covers, and components while
                      restoring a factory-fresh look.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

          </div>
        </div>
      </section>

      {/* 7-Stage Process */}
      <section
        id="process"
        className="py-20 db-section-divider"
      >
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="inline">Our Signature</span>{" "}
              <span className="db-heading-accent bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">
                7-Stage Process
              </span>
            </h2>
            <p className="text-neutral-400 text-lg max-w-4xl mx-auto">
              Systematic excellence that guarantees flawless results meeting
              international standards
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            {MOCK_DATA.process.map((step, index) => (
              <ProcessStep key={index} step={step} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section
        id="why-us"
        className="relative overflow-hidden py-20 db-section-divider"
      >
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-24 -left-20 h-72 w-72 rounded-full bg-orange-500/10 blur-3xl" />
          <div className="absolute top-1/3 -right-24 h-80 w-80 rounded-full bg-amber-500/10 blur-3xl" />
          <div
            className="absolute inset-0 opacity-[0.1]"
            style={{
              backgroundImage:
                "linear-gradient(rgba(249,115,22,0.22) 1px, transparent 1px), linear-gradient(90deg, rgba(249,115,22,0.22) 1px, transparent 1px)",
              backgroundSize: "70px 70px",
              maskImage:
                "radial-gradient(ellipse at center, black 42%, transparent 85%)",
              WebkitMaskImage:
                "radial-gradient(ellipse at center, black 42%, transparent 85%)",
            }}
          />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Why{" "}
              <span className="db-heading-accent bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">
                Detailing Bhai?
              </span>
            </h2>
            <p className="text-neutral-400 text-lg max-w-2xl mx-auto">
              Over 15 years of excellence in premium car care
            </p>
          </div>

          <div className="max-w-5xl mx-auto mb-12">
            <Card className="db-orb-card group relative overflow-hidden bg-gradient-to-br from-white/8 via-neutral-950/92 to-white/10 border-transparent shadow-[inset_0_0_0_1px_rgba(255,255,255,0.05),0_20px_80px_rgba(0,0,0,0.28)]">
              <div className="pointer-events-none absolute -top-36 -left-28 h-80 w-80 rounded-full bg-orange-500/8 blur-xl transition-transform duration-700 group-hover:scale-110" />
              <div className="pointer-events-none absolute -bottom-36 -right-24 h-72 w-72 rounded-full bg-amber-500/7 blur-xl transition-transform duration-700 group-hover:scale-110" />
              <div
                className="pointer-events-none absolute inset-0 opacity-[0.07]"
                style={{
                  backgroundImage:
                    "radial-gradient(circle at 1px 1px, rgba(255,255,255,.35) 1px, transparent 0)",
                  backgroundSize: "18px 18px",
                }}
              />
              <CardContent className="p-6 sm:p-8 lg:p-10 relative z-10">
                <div className="grid lg:grid-cols-[1.5fr_1fr] gap-8 items-start">
                  <div className="text-left">
                    <h3 className="text-3xl sm:text-4xl font-bold text-white leading-tight">
                      Why We Chose The Name{" "}
                      <span className="db-brand-gradient">Detailing Bhai</span>
                    </h3>
                    <p className="mt-4 text-neutral-200 leading-relaxed">
                      In Hindi,{" "}
                      <strong className="text-white">&quot;Bhai&quot;</strong>{" "}
                      means{" "}
                      <strong className="text-white">
                        &quot;Brother&quot;
                      </strong>
                      . That is exactly how we work: like a trusted brother who
                      cares for your vehicle the way you would.
                    </p>
                    <p className="mt-3 text-neutral-300 leading-relaxed">
                      We started with one simple thought:{" "}
                      <strong className="text-white">
                        if this car were our own, what would we do?
                      </strong>{" "}
                      That mindset built our no-compromise standards, our
                      signature 7-stage process, and our focus on recommending
                      what is right for your vehicle, not what is expensive.
                    </p>
                    <p className="mt-3 text-neutral-300 leading-relaxed">
                      The lion in our logo stands for{" "}
                      <strong className="text-white">
                        strength, protection, and confidence
                      </strong>
                      . Our mission is simple: make every owner smile when they
                      see their vehicle after our work.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="db-orb-card group/item rounded-xl border border-white/10 bg-gradient-to-r from-white/5 to-white/10 p-4 transition-all duration-300 hover:-translate-y-1 hover:border-orange-500/50 hover:shadow-[0_18px_50px_rgba(249,115,22,0.12)]">
                      <div className="db-feature-row">
                        <div className="bg-gradient-to-br from-orange-500 to-amber-500 p-2 rounded-lg shadow-md transition-transform duration-300 group-hover/item:scale-105">
                          <Shield className="h-5 w-5 text-white" />
                        </div>
                        <div className="db-feature-copy">
                          <p className="db-feature-title text-white font-semibold">
                            No-Compromise Process
                          </p>
                          <p className="text-neutral-300 text-sm mt-1">
                            Every vehicle goes through our strict 7-stage
                            quality workflow.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="db-orb-card group/item rounded-xl border border-white/10 bg-gradient-to-r from-white/5 to-white/10 p-4 transition-all duration-300 hover:-translate-y-1 hover:border-orange-500/50 hover:shadow-[0_18px_50px_rgba(249,115,22,0.12)]">
                      <div className="db-feature-row">
                        <div className="bg-gradient-to-br from-orange-500 to-amber-500 p-2 rounded-lg shadow-md transition-transform duration-300 group-hover/item:scale-105">
                          <Award className="h-5 w-5 text-white" />
                        </div>
                        <div className="db-feature-copy">
                          <p className="db-feature-title text-white font-semibold">
                            Verified Premium Products
                          </p>
                          <p className="text-neutral-300 text-sm mt-1">
                            We source from trusted vendors and apply only after
                            proper testing.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="db-orb-card group/item rounded-xl border border-white/10 bg-gradient-to-r from-white/5 to-white/10 p-4 transition-all duration-300 hover:-translate-y-1 hover:border-orange-500/50 hover:shadow-[0_18px_50px_rgba(249,115,22,0.12)]">
                      <div className="db-feature-row">
                        <div className="bg-gradient-to-br from-orange-500 to-amber-500 p-2 rounded-lg shadow-md transition-transform duration-300 group-hover/item:scale-105">
                          <Star className="h-5 w-5 text-white" />
                        </div>
                        <div className="db-feature-copy">
                          <p className="db-feature-title text-white font-semibold">
                            Customer-First Vision
                          </p>
                          <p className="text-neutral-300 text-sm mt-1">
                            Your satisfaction, trust, and long-term protection
                            are our top priorities.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {MOCK_DATA.whyChooseUs.map((reason, index) => (
              <Card
                key={index}
                className="db-orb-card relative overflow-hidden bg-gradient-to-r from-white/5 to-white/10 border-white/10 hover:border-orange-500/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_50px_rgba(249,115,22,0.12)]"
              >
                <CardContent className="p-6 relative z-10">
                  <div className="db-feature-row">
                    <div className="bg-gradient-to-br from-orange-500 to-amber-500 p-2.5 sm:p-3 rounded-lg">
                      {reason.icon}
                    </div>
                    <div className="db-feature-copy">
                      <h3 className="db-feature-title text-xl font-bold mb-2 text-white">
                        {reason.title}
                      </h3>
                      <p className="text-neutral-400">{reason.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Facility Images */}
          <div className="grid md:grid-cols-2 gap-6 mt-12 max-w-5xl mx-auto">
            <div className="relative h-64 rounded-xl overflow-hidden group">
              <img
                src="https://images.unsplash.com/photo-1739161763843-4adaf7709291?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwzfHxjYXIlMjBkZXRhaWxpbmclMjBzdHVkaW98ZW58MHx8fHwxNzczNDg0MTU3fDA&ixlib=rb-4.1.0&q=85"
                alt="Detailing Bhai state-of-the-art car detailing facility in Jaipur with professional equipment"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                loading="lazy"
                width="600"
                height="400"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-6">
                <p className="text-white font-semibold text-lg">
                  State-of-the-Art Facility
                </p>
              </div>
            </div>
            <div className="relative h-64 rounded-xl overflow-hidden group">
              <img
                src="https://images.unsplash.com/photo-1711512302274-8aafe96481bb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHxjZXJhbWljJTIwY29hdGluZ3xlbnwwfHx8fDE3NzM0ODQxNDd8MA&ixlib=rb-4.1.0&q=85"
                alt="Dust-free coating room with controlled environment for perfect PPF and ceramic coating application"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                loading="lazy"
                width="600"
                height="400"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-6">
                <p className="text-white font-semibold text-lg">
                  Dust-Free Coating Room
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section - Instagram Integration */}
      <section
        id="gallery"
        ref={gallerySectionRef}
        className="py-20 db-section-divider"
      >
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Our{" "}
              <span className="db-heading-accent bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">
                Work Showcase
              </span>
            </h2>
            <p className="text-neutral-400 text-lg mb-2">
              Follow us on Instagram for daily updates
            </p>
            <a
              href="https://www.instagram.com/detailingbhai/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-[#f58529] via-[#dd2a7b] to-[#8134af] bg-clip-text text-transparent hover:from-[#f8a04f] hover:via-[#e34b90] hover:to-[#9550c4] transition-colors font-bold text-xl"
            >
              <Instagram className="h-6 w-6 text-[#E1306C]" />
              <span>@detailingbhai</span>
            </a>
          </div>

          {/* Instagram Embed Widget Placeholder */}
          <div className="max-w-6xl mx-auto mb-12">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
              <div className="text-center space-y-4">
                <div className="mx-auto inline-flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-[#feda75] via-[#d62976] to-[#4f5bd5] shadow-[0_14px_40px_rgba(214,41,118,0.35)]">
                  <Instagram className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white">
                  Instagram Feed
                </h3>
                <p className="text-neutral-400 max-w-2xl mx-auto">
                  See our latest work on Instagram! Follow{" "}
                  <a
                    href="https://www.instagram.com/detailingbhai/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-gradient-to-r from-[#f58529] via-[#dd2a7b] to-[#8134af] bg-clip-text text-transparent font-bold hover:from-[#f8a04f] hover:via-[#e34b90] hover:to-[#9550c4]"
                  >
                    @detailingbhai
                  </a>{" "}
                  for daily car detailing transformations, ceramic coating
                  applications, and PPF installations.
                </p>

                {shouldLoadInstagram ? (
                  <div className="db-insta-widget mt-6 rounded-2xl border border-white/10 bg-black/85 p-1.5 sm:p-2">
                    <div
                      className="embedsocial-hashtag"
                      data-ref="1fb711a9f4942d2b4a866c439ff822d812d413e3"
                      data-dynamicload="yes"
                      data-lazyload="yes"
                    />
                  </div>
                ) : (
                  <div className="mt-6 rounded-2xl border border-white/10 bg-black/85 py-10 text-center text-neutral-400">
                    Loading latest Instagram posts...
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="text-center">
            <a
              href="https://www.instagram.com/detailingbhai/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 sm:gap-3 whitespace-nowrap bg-gradient-to-r from-[#f58529] via-[#dd2a7b] to-[#8134af] text-white px-5 sm:px-8 py-3 sm:py-4 rounded-full text-sm sm:text-lg font-semibold hover:from-[#de7422] hover:via-[#c42569] hover:to-[#6f2e96] transition-all hover:scale-105"
            >
              <Instagram className="h-5 w-5 sm:h-6 sm:w-6" />
              <span className="sm:hidden">Follow @detailingbhai</span>
              <span className="hidden sm:inline">
                Follow @detailingbhai on Instagram
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section
        id="testimonials"
        className="py-20 db-section-divider"
      >
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="mb-5 flex flex-col items-center justify-center gap-4 md:mb-4 md:flex-row md:gap-3">
              <h2 className="text-4xl md:text-5xl font-bold leading-tight">
                What Our{" "}
                <span className="db-heading-accent bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">
                  Customers Say
                </span>
              </h2>
              <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-full flex items-center gap-2 px-5 py-2.5 md:px-6 md:py-3">
                <Star className="h-5 w-5 md:h-6 md:w-6 text-white fill-white" />
                <span className="text-white font-bold text-xl md:text-2xl">
                  5.0
                </span>
              </div>
            </div>
            <p className="text-neutral-400 text-base md:text-lg max-w-4xl mx-auto leading-relaxed">
              Rated 5 Stars on Google Reviews | Real reviews from real customers
              who trusted us with their vehicles
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {MOCK_DATA.testimonials.map((testimonial, index) => (
              <Card
                key={index}
                className="db-orb-card bg-gradient-to-br from-white/5 to-white/10 border-white/10 hover:border-orange-500/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_50px_rgba(249,115,22,0.15)]"
              >
                <CardContent className="p-6">
                  {/* Rating Stars */}
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star
                        key={i}
                        className="h-5 w-5 fill-orange-500 text-orange-500"
                      />
                    ))}
                  </div>

                  {/* Review Text */}
                  <p className="text-neutral-300 italic mb-6 leading-relaxed">
                    "{testimonial.review}"
                  </p>

                  {/* Customer Info */}
                  <div className="border-t border-white/10 pt-4">
                    <p className="text-white font-bold text-lg">
                      {testimonial.name}
                    </p>
                    <p className="text-orange-500 font-semibold">
                      {testimonial.car}
                    </p>
                    <p className="text-neutral-300 text-sm">
                      {testimonial.service}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <a
              href="https://www.google.com/search?q=detailing+bhai+jaipur+reviews"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 whitespace-nowrap bg-white/10 hover:bg-white/20 text-white px-5 sm:px-8 py-3 sm:py-4 rounded-full text-sm sm:text-lg font-semibold border border-white/20 transition-all hover:scale-105"
            >
              <Star className="h-5 w-5 sm:h-6 sm:w-6 text-orange-500" />
              <span className="sm:hidden">Read Google Reviews</span>
              <span className="hidden sm:inline">Read More Reviews on Google</span>
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 db-section-divider">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Frequently Asked{" "}
              <span className="db-heading-accent bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">
                Questions
              </span>
            </h2>
          </div>

          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-6">
              {MOCK_DATA.faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-white/5 border border-white/10 rounded-lg px-6 py-2 hover:bg-white/10 transition-colors"
                >
                  <AccordionTrigger className="text-white hover:text-orange-500 text-left py-6 hover:no-underline text-lg">
                    <span className="font-semibold">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="text-neutral-400 pb-6 pt-2 text-base">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section
        id="contact"
        className="py-20 db-section-divider"
      >
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Get in{" "}
              <span className="db-heading-accent bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">
                Touch
              </span>
            </h2>
            <p className="text-neutral-400 text-lg mb-8">
              Ready to give your car the care it deserves?
            </p>
            <p className="text-white text-xl font-semibold mb-8">
              Contact us directly for immediate assistance
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {/* Primary CTA Buttons */}
            <div className="grid md:grid-cols-2 gap-6 mb-12">
              <a href="tel:9773301727" className="group">
                <Card className="bg-gradient-to-br from-orange-500/20 to-amber-500/20 border-orange-500/50 hover:border-orange-500 transition-all hover:scale-105">
                  <CardContent className="p-8 text-center">
                    <div className="bg-gradient-to-br from-orange-500 to-amber-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                      <Phone className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">
                      Call Us Now
                    </h3>
                    <p className="text-3xl font-bold text-orange-500 mb-2">
                      9773301727
                    </p>
                    <p className="text-neutral-400">
                      Speak with our experts immediately
                    </p>
                  </CardContent>
                </Card>
              </a>

              <a
                href="https://wa.me/919773301727"
                target="_blank"
                rel="noopener noreferrer"
                className="group"
              >
                <Card className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 border-green-500/50 hover:border-green-500 transition-all hover:scale-105">
                  <CardContent className="p-8 text-center">
                    <div className="bg-gradient-to-br from-green-500 to-emerald-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                      <WhatsAppIcon className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">
                      WhatsApp Us
                    </h3>
                    <p className="text-3xl font-bold text-green-500 mb-2">
                      Chat Now
                    </p>
                    <p className="text-neutral-400">
                      Quick response on WhatsApp
                    </p>
                  </CardContent>
                </Card>
              </a>
            </div>

            {/* Google Maps */}
            <div className="mb-12 rounded-2xl overflow-hidden border border-white/10">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1371!2d75.7104662!3d26.9237143!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x7a5e69352d85393%3A0x535adb2136193423!2sDetailing%20Bhai!5e0!3m2!1sen!2sin!4v1710000000000!5m2!1sen!2sin"
                width="100%"
                height="350"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Detailing Bhai Location - Jaipur, Rajasthan"
                className="w-full"
              ></iframe>
            </div>

            {/* Contact Information */}
            <Card className="bg-gradient-to-br from-white/5 to-white/10 border-white/10 max-w-6xl mx-auto">
              <CardContent className="p-8">
                <div className="grid md:grid-cols-2 gap-8 mb-8">
                  {/* Left Column - Phone & Email */}
                  <div className="space-y-6">
                    <div className="flex items-start space-x-3">
                      <Phone className="h-6 w-6 text-orange-500 mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-white font-semibold text-lg mb-1">
                          Phone
                        </p>
                        <a
                          href="tel:9773301727"
                          className="text-neutral-300 hover:text-orange-500 transition-colors text-lg"
                        >
                          +91 9773301727
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <Mail className="h-6 w-6 text-orange-500 mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-white font-semibold text-lg mb-1">
                          Email
                        </p>
                        <a
                          href="mailto:hello@detailingbhai.in"
                          className="text-neutral-300 hover:text-orange-500 transition-colors"
                        >
                          hello@detailingbhai.in
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Right Column - Location & Hours */}
                  <div className="space-y-6">
                    <div className="flex items-start space-x-3">
                      <MapPin className="h-6 w-6 text-orange-500 mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-white font-semibold text-lg mb-1">
                          Location
                        </p>
                        <p className="text-neutral-300">
                          A-6, A-7, Meenawala, Sirsi Road, Jaipur - 302034
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <Clock className="h-6 w-6 text-orange-500 mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-white font-semibold text-lg mb-1">
                          Working Hours
                        </p>
                        <p className="text-neutral-300">
                          All Days: 9:30 AM - 8:00 PM
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Social Media Section */}
                <div className="border-t border-white/10 pt-8">
                  <h3 className="text-2xl font-bold text-white mb-6 text-center">
                    Connect With Us
                  </h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <a
                      href="https://www.instagram.com/detailingbhai/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-3 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 p-4 rounded-xl transition-all hover:scale-105"
                      aria-label="Follow us on Instagram"
                    >
                      <Instagram className="h-7 w-7 text-white" />
                      <span className="text-white font-semibold text-lg">
                        Instagram
                      </span>
                    </a>
                    <a
                      href="https://www.youtube.com/@DetailingBhai"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-3 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 p-4 rounded-xl transition-all hover:scale-105"
                      aria-label="Subscribe on YouTube"
                    >
                      <Youtube className="h-7 w-7 text-white" />
                      <span className="text-white font-semibold text-lg">
                        YouTube
                      </span>
                    </a>
                    <a
                      href="https://www.facebook.com/DetailingBhai/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 p-4 rounded-xl transition-all hover:scale-105"
                      aria-label="Like us on Facebook"
                    >
                      <Facebook className="h-7 w-7 text-white" />
                      <span className="text-white font-semibold text-lg">
                        Facebook
                      </span>
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      </div>

      {/* Footer */}
      <footer
        className="bg-black db-gradient-top-rule py-12"
        role="contentinfo"
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col items-center space-y-6">
              <div className="flex items-center space-x-3">
                <img
                  src="/img/logo-site.webp"
                  alt="Detailing Bhai"
                  className="h-16 w-16 object-contain"
                  width="64"
                  height="64"
                />
                <div>
                  <p className="db-brand-gradient text-[1.78rem] font-bold">
                    DETAILING BHAI
                  </p>
                  <p className="text-sm text-neutral-400">
                    Jaipur's Trusted Car Detailing Studio
                  </p>
                </div>
              </div>

              <p className="text-neutral-400 text-center max-w-4xl mx-auto leading-relaxed">
                Premium car detailing services in Jaipur offering ceramic
                coating, PPF installation, paint correction, and complete car
                care solutions. With 15+ years of experience, we provide
                quality-first protection packages with clear warranty terms,
                including eligible long-term and lifetime PPF coverage. Trust{" "}
                <span className="db-brand-gradient">Detailing Bhai</span> for
                professional ceramic coating in Jaipur, paint
                protection film (PPF), graphene coating, and interior detailing
                services.
              </p>
            </div>
          </div>
        </div>
        <div className="db-gradient-top-rule pt-6 w-full mt-12">
          <div className="container mx-auto px-4">
            <div className="mb-3 flex flex-wrap items-center justify-center gap-4 text-sm">
              <a
                href="/privacy-policy/"
                className="text-neutral-400 hover:text-orange-400 transition-colors"
              >
                Privacy Policy
              </a>
              <span className="text-neutral-600">|</span>
              <a
                href="/terms-and-conditions/"
                className="text-neutral-400 hover:text-orange-400 transition-colors"
              >
                Terms &amp; Conditions
              </a>
            </div>
            <p className="text-neutral-300 text-sm text-center">
              &copy; 2026 <span className="db-brand-gradient">Detailing Bhai</span>. All
              rights reserved. | Car Detailing Jaipur | Ceramic Coating | PPF
              Services
            </p>
          </div>
        </div>
      </footer>

      {/* Floating Action Buttons */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-4">
        <a
          href="tel:9773301727"
          className="db-fab db-fab--call bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white p-4 rounded-full shadow-2xl transition-all hover:scale-110 flex items-center justify-center"
          aria-label="Call us"
        >
          <Phone className="h-6 w-6" />
        </a>
        <a
          href="https://wa.me/919773301727"
          target="_blank"
          rel="noopener noreferrer"
          className="db-fab db-fab--wa bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white p-4 rounded-full shadow-2xl transition-all hover:scale-110 flex items-center justify-center"
          aria-label="Contact on WhatsApp"
        >
          <WhatsAppIcon className="h-6 w-6" />
        </a>
      </div>
    </div>
  );
};

export default Home;
